import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
  requireAdmin?: boolean;
  allowUnverified?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  redirectTo = '/login',
  requireAdmin = false,
  allowUnverified = false,
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  const adminEmail = (process.env.REACT_APP_ADMIN_EMAIL || '').trim().toLowerCase();
  const userEmail = user?.email ? user.email.trim().toLowerCase() : '';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (requireAuth && !user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (!requireAuth && user) {
    if (!user.emailVerified && !allowUnverified) {
      return <Navigate to="/verify-email" replace />;
    }
    const from = location.state?.from?.pathname || '/upload';
    return <Navigate to={from} replace />;
  }

  if (requireAuth && user && !allowUnverified && !user.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  if (requireAdmin && (!userEmail || !adminEmail || userEmail !== adminEmail)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">Page not found</h2>
          <p className="text-gray-600 mb-8">The page you are looking for does not exist.</p>
          <a href="/" className="btn-primary">
            Go home
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
